import { useLiveQuery } from "dexie-react-hooks";
import { ArrowUp, Bell, Calendar, CircleHelp, Flag, Hash, Inbox, Mic, Repeat, Tag } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  colorHex, parseQuickAdd, toISODate, quickAddTokens, removeQuickAddToken, type Command, type Due, type Label, type Priority, type Project, type QuickAddToken,
} from "@todoer/shared";
import { db, getUser } from "../lib/db.js";
import { cmd, dispatch, uuid } from "../lib/sync.js";
import { describeDue, DuePicker } from "./DuePicker.js";
import { Dialog, Popover } from "./overlay.js";
import { LabelList, PRIORITIES, priorityMeta, PriorityList, ProjectList, ReminderPanel, offsetLabel, type ReminderDraft } from "./pickers.js";
import { Button, cx, IconButton, useIsPhone } from "./ui.js";

interface Props {
  onClose: () => void;
  /** Pre-select a project (e.g. when opened from a project page) */
  projectId?: string | null;
  sectionId?: string | null;
  /** Add as a sub-task of this task */
  parentId?: string | null;
  /** Pre-set due date (e.g. today when adding from the Today view) */
  defaultDue?: Due | null;
  inline?: boolean;
}

type SpeechRec = { lang: string; interimResults: boolean; continuous: boolean; start(): void; stop(): void; onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null; onend: (() => void) | null; onerror: (() => void) | null };
const SpeechRecognitionCtor = (): (new () => SpeechRec) | null => {
  const w = window as unknown as { SpeechRecognition?: new () => SpeechRec; webkitSpeechRecognition?: new () => SpeechRec };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
};

/** Builds the commands for one quick-add line, creating any new labels. */
export function commandsForLine(
  text: string, opts: {
    projects: Project[]; labels: Label[]; fallbackProjectId: string; sectionId?: string | null; parentId?: string | null; due?: Due | null; priority?: Priority;
    description?: string; order: number; id?: string; labelIds?: string[];
  },
): Command[] | null {
  const parsed = parseQuickAdd(text);
  if (!parsed.content) return null;
  const commands: Command[] = [];
  const labelIds: string[] = [...(opts.labelIds ?? [])];
  for (const name of parsed.labels) {
    const existing = opts.labels.find((l) => l.name.toLowerCase() === name.toLowerCase());
    if (existing) { if (!labelIds.includes(existing.id)) labelIds.push(existing.id); }
    else {
      const id = uuid();
      labelIds.push(id);
      opts.labels.push({ id, name, color: "charcoal", itemOrder: 0, isFavorite: false, version: 0, updatedAt: "", deletedAt: null });
      commands.push(cmd("label_add", { id, name }));
    }
  }
  const named = parsed.projectName ? opts.projects.find((p) => (p.isInbox ? "inbox" : p.name.toLowerCase()) === parsed.projectName!.toLowerCase()) : undefined;
  const projectId = named?.id ?? opts.fallbackProjectId;
  commands.push(cmd("item_add", {
    id: opts.id ?? uuid(), projectId, sectionId: projectId === opts.fallbackProjectId ? opts.sectionId ?? null : null, parentId: opts.parentId ?? null,
    content: parsed.content, description: opts.description ?? "", priority: parsed.priority ?? opts.priority ?? 1,
    due: parsed.due ?? opts.due ?? null, labelIds, childOrder: opts.order,
  }));
  return commands;
}

const tokenStyle: Record<QuickAddToken["kind"], string> = {
  date: "bg-success-soft text-success",
  project: "bg-brand-soft text-brand-text",
  label: "bg-[color-mix(in_srgb,var(--color-info)_14%,transparent)] text-info",
  priority: "bg-[color-mix(in_srgb,var(--color-p2)_16%,transparent)] text-warning",
};

/** The text with recognised tokens wrapped in <mark>, drawn behind the transparent textarea. */
function Highlighted({ text, tokens }: { text: string; tokens: QuickAddToken[] }) {
  const out: ReactNode[] = [];
  let at = 0;
  for (const t of tokens) {
    if (t.start < at) continue;
    out.push(text.slice(at, t.start));
    out.push(<mark key={t.start} data-token={t.kind} className={cx("rounded-[3px]", tokenStyle[t.kind])}>{text.slice(t.start, t.end)}</mark>);
    at = t.end;
  }
  out.push(text.slice(at));
  // A trailing newline needs a character after it to take up a line.
  return <>{out}{"​"}</>;
}

type Picker = "date" | "priority" | "project" | "labels" | "reminder";

/**
 * Todoist-style quick add. Type naturally ("Pay rent every 1st p2 #Home @finance"):
 * recognised parts are highlighted as you type and listed as chips below. The pill
 * buttons set the same things with pickers. Anything typed wins over the pills.
 * Pasting several lines offers to add one task per line.
 */
export function QuickAdd({ onClose, projectId = null, sectionId = null, parentId = null, defaultDue = null, inline = false }: Props) {
  const [text, setText] = useState("");
  const [description, setDescription] = useState("");
  const [descFocused, setDescFocused] = useState(false);
  const [pickedDue, setPickedDue] = useState<Due | null>(defaultDue);
  const [pickedPriority, setPickedPriority] = useState<Priority>(1);
  const [pickedProjectId, setPickedProjectId] = useState<string | null>(projectId);
  const [pickedSectionId, setPickedSectionId] = useState<string | null>(sectionId);
  const [pickedLabels, setPickedLabels] = useState<string[]>([]);
  const [reminders, setReminders] = useState<ReminderDraft[]>([]);
  const [picker, setPicker] = useState<{ kind: Picker; anchor: HTMLElement } | null>(null);
  const [bulk, setBulk] = useState<string[] | null>(null);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const recRef = useRef<SpeechRec | null>(null);
  const isPhone = useIsPhone();
  const projects = useLiveQuery(() => db.projects.filter((p) => !p.isArchived).toArray(), []);
  const labels = useLiveQuery(() => db.labels.toArray(), []);
  const parent = useLiveQuery(() => (parentId ? db.tasks.get(parentId) : undefined), [parentId]);
  const parsed = useMemo(() => parseQuickAdd(text), [text]);
  const tokens = useMemo(() => quickAddTokens(text), [text]);
  const canListen = useMemo(() => !!SpeechRecognitionCtor(), []);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => () => recRef.current?.stop(), []);
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  const inbox = projects?.find((p) => p.isInbox) ?? null;
  const typedProject = useMemo(() => (parsed.projectName
    ? projects?.find((p) => (p.isInbox ? "inbox" : p.name.toLowerCase()) === parsed.projectName!.toLowerCase()) ?? null : null), [parsed.projectName, projects]);
  const targetProject = useMemo(() => {
    if (parent) return projects?.find((p) => p.id === parent.projectId) ?? null;
    return typedProject ?? projects?.find((p) => p.id === pickedProjectId) ?? inbox;
  }, [typedProject, pickedProjectId, projects, inbox, parent]);

  const due = parsed.due ?? pickedDue;
  const priority = parsed.priority ?? pickedPriority;
  const typedLabelIds = (labels ?? []).filter((l) => parsed.labels.some((n) => n.toLowerCase() === l.name.toLowerCase())).map((l) => l.id);
  const labelCount = new Set([...pickedLabels, ...typedLabelIds]).size + parsed.labels.filter((n) => !(labels ?? []).some((l) => l.name.toLowerCase() === n.toLowerCase())).length;

  const removeToken = (kind: QuickAddToken["kind"], only?: QuickAddToken) => {
    let next = text;
    const list = only ? [only] : [...tokens].filter((t) => t.kind === kind);
    for (const t of list.sort((a, b) => b.start - a.start)) next = removeQuickAddToken(next, t);
    setText(next);
  };

  const reset = () => {
    setText(""); setDescription(""); setPickedDue(defaultDue); setPickedPriority(1); setPickedLabels([]); setReminders([]); setBulk(null); setPicker(null);
    if (inline) inputRef.current?.focus(); else onClose();
  };

  const run = async (fn: () => Promise<void>) => {
    try { setError(null); await fn(); } catch (e) {
      console.error("add task failed", e);
      setError(e instanceof Error ? e.message : "Could not add the task");
    }
  };

  const fallbackProject = async () => {
    const user = await getUser();
    const pid = targetProject?.id ?? user?.inboxProjectId;
    if (!pid) throw new Error("Still loading your projects. Try again in a moment.");
    return pid;
  };

  const submit = () => run(async () => {
    if (!parsed.content) return;
    const pid = await fallbackProject();
    const order = (await db.tasks.where("projectId").equals(pid).count()) + 1;
    const id = uuid();
    const commands = commandsForLine(text, {
      projects: projects ?? [], labels: [...(labels ?? [])], fallbackProjectId: pid,
      sectionId: pid === pickedProjectId ? pickedSectionId : pid === projectId ? sectionId : null,
      parentId, due: pickedDue, priority: pickedPriority, description: description.trim(), order, id, labelIds: pickedLabels,
    });
    if (!commands) return;
    for (const r of reminders) commands.push(cmd("reminder_add", { id: uuid(), taskId: id, ...r }));
    await dispatch(...commands);
    reset();
  });

  const addBulk = () => run(async () => {
    if (!bulk) return;
    const pid = await fallbackProject();
    let order = await db.tasks.where("projectId").equals(pid).count();
    const known = [...(labels ?? [])];
    const all: Command[] = [];
    for (const line of bulk) {
      const c = commandsForLine(line, { projects: projects ?? [], labels: known, fallbackProjectId: pid, sectionId: pid === pickedProjectId ? pickedSectionId : null, parentId, due: pickedDue, priority: pickedPriority, order: ++order, labelIds: pickedLabels });
      if (c) all.push(...c);
    }
    await dispatch(...all);
    reset();
  });

  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const lines = e.clipboardData.getData("text").split(/\r?\n/).map((l) => l.replace(/^\s*(?:[-*•]|\d+[.)]|\[[ x]\])\s+/i, "").trim()).filter(Boolean);
    if (lines.length > 1) { e.preventDefault(); setBulk(lines); }
  };

  const toggleVoice = () => {
    if (listening) { recRef.current?.stop(); return; }
    const Ctor = SpeechRecognitionCtor();
    if (!Ctor) return;
    const rec = new Ctor();
    rec.lang = navigator.language || "en-GB";
    rec.interimResults = false;
    rec.continuous = false;
    rec.onresult = (e) => {
      const said = Array.from(e.results).map((r) => r[0]?.transcript ?? "").join(" ").trim();
      if (said) setText((t) => (t ? `${t} ${said}` : said));
    };
    rec.onend = () => { setListening(false); inputRef.current?.focus(); };
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    rec.start();
  };

  const openPicker = (kind: Picker) => (e: { currentTarget: HTMLElement }) => {
    const anchor = e.currentTarget;
    setPicker((p) => (p?.kind === kind ? null : { kind, anchor }));
  };
  const closePicker = () => { setPicker(null); inputRef.current?.focus(); };
  const pm = priorityMeta(priority);
  const titleFont = "text-lead font-medium phone:text-[17px] phone:leading-6";

  const pill = (kind: Picker, icon: ReactNode, label: string, opts: { active?: boolean; tone?: string; aria: string } = { aria: label }) => (
    <button type="button" onClick={openPicker(kind)} aria-label={opts.aria} aria-haspopup="dialog" aria-expanded={picker?.kind === kind}
      className={cx("inline-flex h-8 shrink-0 items-center gap-1.5 rounded-control border px-2.5 text-meta font-medium transition-colors duration-150 phone:h-11 phone:px-3",
        opts.active ? "border-border-strong bg-surface-raised text-text" : "border-border text-text-muted hover:border-border-strong hover:text-text",
        picker?.kind === kind && "border-brand ring-2 ring-brand/20", opts.tone)}>
      {icon}<span className="max-w-40 truncate">{label}</span>
    </button>
  );

  const chips = tokens.length > 0 && (
    <div className="flex flex-wrap gap-1.5" aria-label="Recognised in the task name">
      {tokens.map((t) => {
        const kind = t.kind;
        const label = kind === "date" && parsed.due ? describeDue(parsed.due) : t.text;
        const Icon = kind === "date" ? (parsed.due?.isRecurring ? Repeat : Calendar) : kind === "project" ? Hash : kind === "label" ? Tag : Flag;
        const pickerKind: Picker = kind === "date" ? "date" : kind === "project" ? "project" : kind === "label" ? "labels" : "priority";
        return (
          <span key={`${t.kind}${t.start}`} className={cx("inline-flex items-center rounded-full text-meta font-medium", tokenStyle[kind])}>
            <button type="button" onClick={openPicker(pickerKind)} className="inline-flex items-center gap-1 py-1 pl-2.5 pr-1.5 phone:min-h-11 phone:pl-3" aria-label={`Change ${kind}: ${label}`}>
              <Icon size={12} aria-hidden />{label}
            </button>
            <button type="button" onClick={() => { removeToken(kind, t); inputRef.current?.focus(); }} aria-label={`Remove ${label}`}
              className="mr-1 grid h-5 w-5 place-items-center rounded-full hover:bg-black/10 phone:mr-0 phone:h-11 phone:w-10 dark:hover:bg-white/10"><span aria-hidden className="leading-none">×</span></button>
          </span>
        );
      })}
    </div>
  );

  const form = (
    <form onSubmit={(e) => { e.preventDefault(); void submit(); }}
      className={cx("flex flex-col", inline ? "rounded-card border border-border-strong bg-surface-raised shadow-pop" : "")}>
      <div className={cx("px-5 pt-4 phone:px-4 phone:pt-1", inline && "px-4 pt-3")}>
        {parent && <p className="mb-1 truncate text-meta text-text-faint">Sub-task of “{parent.content}”</p>}
        <div className="relative">
          <div aria-hidden className={cx("pointer-events-none absolute inset-0 whitespace-pre-wrap break-words text-text", titleFont)} data-testid="quick-add-highlight">
            <Highlighted text={text} tokens={tokens} />
          </div>
          <textarea ref={inputRef} rows={1} value={text} onChange={(e) => setText(e.target.value.replace(/\n/g, " "))} onPaste={onPaste}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) { e.preventDefault(); void submit(); } }}
            placeholder={parentId ? "Sub-task name" : "Task name, e.g. Call Sam tomorrow 3pm p1 #Work"} aria-label="Task name"
            className={cx("relative block w-full resize-none overflow-hidden bg-transparent text-transparent caret-text outline-none placeholder:text-text-faint selection:bg-brand/25 selection:text-transparent", titleFont)} />
        </div>
        {!bulk && (
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} onFocus={() => setDescFocused(true)} onBlur={() => setDescFocused(false)}
            placeholder="Description" aria-label="Description" rows={descFocused || description.includes("\n") ? 3 : 1}
            className="mt-1 block w-full resize-none bg-transparent text-body text-text-muted outline-none placeholder:text-text-faint" />
        )}
        {chips && <div className="mt-2">{chips}</div>}
        {bulk && (
          <div className="mt-2 rounded-card border border-border bg-surface-sunken p-3">
            <p className="text-body font-medium">Add {bulk.length} tasks, one per line?</p>
            <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-body text-text-muted">
              {bulk.map((l, i) => <li key={i} className="flex items-center gap-2"><span aria-hidden className="h-3.5 w-3.5 shrink-0 rounded-full border-[1.5px] border-border-strong" />{l}</li>)}
            </ul>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="primary" size="sm" onClick={() => void addBulk()}>Add {bulk.length} tasks</Button>
              <Button size="sm" onClick={() => { setText(bulk.join(" ")); setBulk(null); }}>Paste as one task</Button>
              <Button size="sm" variant="ghost" onClick={() => setBulk(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </div>

      <div className={cx("no-scrollbar mt-3 flex items-center gap-1.5 overflow-x-auto px-5 pb-3 phone:px-4", inline && "px-4")}>
        {pill("date", due?.isRecurring ? <Repeat size={14} aria-hidden /> : <Calendar size={14} aria-hidden />, due ? describeDue(due) : "Date",
          { active: !!due, tone: due ? (due.date < toISODate(new Date()) ? "text-danger" : "text-success") : undefined, aria: due ? `Date: ${describeDue(due)}` : "Date" })}
        {pill("priority", <Flag size={14} aria-hidden style={{ color: priority > 1 ? pm.color : undefined }} fill={priority > 1 ? pm.color : "none"} />, priority > 1 ? pm.short : "Priority",
          { active: priority > 1, aria: `Priority: ${pm.label}` })}
        {!parentId && pill("project", targetProject?.isInbox || !targetProject ? <Inbox size={14} aria-hidden className="text-info" /> : <span aria-hidden className="h-2.5 w-2.5 rounded-full" style={{ background: colorHex(targetProject.color) }} />,
          targetProject ? (targetProject.isInbox ? "Inbox" : targetProject.name) : "Inbox", { active: true, aria: `Project: ${targetProject?.isInbox ? "Inbox" : targetProject?.name ?? "Inbox"}` })}
        {pill("labels", <Tag size={14} aria-hidden />, labelCount ? `${labelCount} label${labelCount === 1 ? "" : "s"}` : "Labels", { active: labelCount > 0, aria: "Labels" })}
        {pill("reminder", <Bell size={14} aria-hidden />, reminders.length ? `${reminders.length} reminder${reminders.length === 1 ? "" : "s"}` : "Reminder", { active: reminders.length > 0, aria: "Reminders" })}
      </div>

      {error && <p className="px-5 pb-2 text-meta text-danger">{error}</p>}

      <div className={cx("flex items-center gap-2 border-t border-border px-5 py-3 phone:px-4 phone:py-2", inline && "px-4")}>
        <span tabIndex={0} data-tip={'Type "tomorrow 9am", "every mon", p1, #Project or @label. Paste a list to add many.'} aria-label="Quick add tips"
          className="grid h-8 w-8 place-items-center rounded-control text-text-faint hover:bg-hover hover:text-text phone:hidden"><CircleHelp size={16} aria-hidden /></span>
        {canListen && <IconButton icon={Mic} label={listening ? "Stop listening" : "Speak a task"} onClick={toggleVoice} active={listening} className={cx(listening && "animate-pulse")} />}
        <span className="ml-auto flex items-center gap-2">
          {!isPhone && <Button variant="ghost" onClick={onClose}>Cancel</Button>}
          {isPhone && !inline
            ? <button type="submit" disabled={!parsed.content || !!bulk} aria-label={parentId ? "Add sub-task" : "Add task"}
                className="grid h-10 w-10 place-items-center rounded-full bg-brand-strong text-white transition-opacity disabled:opacity-40"><ArrowUp size={20} aria-hidden /></button>
            : <Button type="submit" variant="primary" disabled={!parsed.content || !!bulk} shortcut="Enter">{parentId ? "Add sub-task" : "Add task"}</Button>}
          {isPhone && inline && <Button variant="ghost" onClick={onClose}>Cancel</Button>}
        </span>
      </div>

      {picker?.kind === "date" && (
        <Popover anchor={picker.anchor} onClose={closePicker} title="Date" width={300}>
          <DuePicker value={due} onChange={(d) => { if (parsed.due) removeToken("date"); setPickedDue(d); }} onPicked={closePicker} />
        </Popover>
      )}
      {picker?.kind === "priority" && (
        <Popover anchor={picker.anchor} onClose={closePicker} title="Priority" width={200}>
          <PriorityList value={priority} onSelect={(p) => { if (parsed.priority) removeToken("priority"); setPickedPriority(p); closePicker(); }} />
        </Popover>
      )}
      {picker?.kind === "project" && (
        <Popover anchor={picker.anchor} onClose={closePicker} title="Project" width={260}>
          <ProjectList withSections value={targetProject?.id ?? null} sectionValue={pickedSectionId} onSelect={(pid, sid) => {
            if (parsed.projectName) removeToken("project");
            setPickedProjectId(pid); setPickedSectionId(sid); closePicker();
          }} />
        </Popover>
      )}
      {picker?.kind === "labels" && (
        <Popover anchor={picker.anchor} onClose={closePicker} title="Labels" width={260}>
          <LabelList selected={[...new Set([...pickedLabels, ...typedLabelIds])]} onChange={(ids) => {
            const removedTyped = typedLabelIds.filter((id) => !ids.includes(id));
            if (removedTyped.length) {
              const names = (labels ?? []).filter((l) => removedTyped.includes(l.id)).map((l) => `@${l.name}`.toLowerCase());
              let next = text;
              for (const t of [...tokens].filter((x) => x.kind === "label" && names.includes(x.text.toLowerCase())).sort((a, b) => b.start - a.start)) next = removeQuickAddToken(next, t);
              setText(next);
            }
            setPickedLabels(ids.filter((id) => !typedLabelIds.includes(id) || pickedLabels.includes(id)));
          }} />
        </Popover>
      )}
      {picker?.kind === "reminder" && (
        <Popover anchor={picker.anchor} onClose={closePicker} title="Reminders" width={300}>
          <ReminderPanel hasDueTime={!!due?.datetime} dueTime={due?.datetime}
            reminders={reminders.map((r, i) => ({ key: String(i), text: r.type === "relative" ? offsetLabel(r.minuteOffset) : new Date(r.at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) }))}
            onAdd={(r) => setReminders((rs) => [...rs, r])} onRemove={(k) => setReminders((rs) => rs.filter((_, i) => String(i) !== k))} />
        </Popover>
      )}
    </form>
  );

  if (inline) return form;
  return (
    <Dialog title={parentId ? "Add sub-task" : "Add task"} onClose={onClose} size="lg" placement="top" bodyClassName="p-0" header={<span />}>
      {form}
    </Dialog>
  );
}

export { PRIORITIES };
